import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { KpiCard, PageHeader } from "@/components/KpiCard";
import { download } from "@/components/phones/PhoneRegister";
import { ForecastTable } from "@/components/forecast/ForecastTable";
import { StoppedPanel } from "@/components/forecast/StoppedPanel";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const ImportButton = ({ onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/forecast/import", fd);
      toast.success(`${data.kind} caricato: ${data.stations.map((s) => `${s.name} ${s.days} giorni`).join(", ")}`, { duration: 7000 });
      if (data.unknown.length) toast.warning(`Stazioni non presenti nell'app, ignorate: ${data.unknown.join(", ")}`, { duration: 9000 });
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ""; }} data-testid="forecast-import-input" />
      <Button onClick={() => ref.current?.click()} disabled={busy} data-testid="forecast-import-button"><Upload className="mr-2 h-4 w-4" /> {busy ? "Caricamento..." : "Carica file FC / Same Day"}</Button>
    </>
  );
};

export default function ForecastPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/stations").then((r) => { setStations(r.data); if (isAdmin && r.data[0]) setStationId((s) => s || r.data[0].id); }); }, [isAdmin]);
  const sid = isAdmin ? stationId : user.station_id;
  const load = useCallback(() => {
    if (sid) api.get("/forecast", { params: { station_id: isAdmin ? sid : undefined } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [sid, isAdmin]);
  useEffect(() => { load(); }, [load]);
  const next = data?.days.find((d) => !d.past);

  return (
    <div className="fade-up">
      <PageHeader title="Forecast furgoni" subtitle="Rotte previste dal forecast confrontate con i furgoni disponibili, i fermi e i rientri presunti dalla manutenzione.">
        {sid && <Button variant="outline" onClick={() => download("/forecast/export", { station_id: isAdmin ? sid : undefined }, "forecast.xlsx")} data-testid="forecast-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarica Excel</Button>}
        {isAdmin && <Button variant="outline" onClick={() => download("/forecast/export-all", {}, "forecast_tutte_stazioni.xlsx")} data-testid="forecast-export-all-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarico generale stazioni</Button>}
        {isAdmin && !user.supervisor && <ImportButton onDone={load} />}
      </PageHeader>
      {isAdmin && (
        <div className="mb-5 flex gap-2 overflow-x-auto pb-1" data-testid="forecast-station-tabs">
          {stations.map((st) => (
            <button key={st.id} onClick={() => setStationId(st.id)} data-testid={`forecast-tab-${st.id}`}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${stationId === st.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>{st.name}</button>
          ))}
        </div>
      )}
      {data && (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiCard label="Furgoni disponibili" value={data.available} accent="#059669" testId="forecast-kpi-available" />
            <KpiCard label="Fermi" value={data.stopped_count} accent="#DC2626" testId="forecast-kpi-stopped" />
            <KpiCard label="Rientri presunti" value={data.returns_planned} accent="#2563EB" testId="forecast-kpi-returns" />
            <KpiCard label={next ? `Disavanzo ${new Date(next.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}` : "Disavanzo"}
              value={next ? (next.diff_returns > 0 ? `+${next.diff_returns}` : next.diff_returns) : "—"} accent={next && next.diff_returns < 0 ? "#DC2626" : "#0F172A"} testId="forecast-kpi-next" />
          </div>
          <div className="grid gap-6 [&>*]:min-w-0 xl:grid-cols-[1fr_340px]">
            <ForecastTable days={data.days} isAdmin={isAdmin} />
            <StoppedPanel items={data.stopped} rescue={data.rescue} onChanged={load} readOnly={user.supervisor} />
          </div>
        </>
      )}
    </div>
  );
}
